(() => {
  if (customElements.get('ikao-popup')) return;
  class IkaoPopup extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.dialog = this.querySelector('dialog');
      this.config = JSON.parse(this.querySelector('[data-popup-config]').textContent);
      this.key = `ikao-popup:${this.config.id}:${this.config.campaign}`;
      this.timers = new Set();
      this.on(this.dialog, 'close', () => this.releaseLock());
      this.on(this.dialog, 'cancel', (event) => { event.preventDefault(); this.close(); });
      this.on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog && this.config.backdropClose) this.close();
      });
      this.on(this, 'click', (event) => {
        if (event.target.closest('[data-popup-close]')) this.close();
        const copy = event.target.closest('[data-popup-copy]');
        if (copy) this.copy(copy);
      });
      this.on(document, 'click', (event) => {
        const link = event.target.closest('a[href]');
        if (link && link.getAttribute('href') === `#ikao-popup-${this.config.campaign}`) {
          event.preventDefault(); this.requestOpen(true);
        }
      });
      this.on(document, 'shopify:section:select', (event) => {
        if (event.detail.sectionId === this.config.id) this.requestOpen(true);
      });
      this.on(document, 'shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.config.id) this.close(false);
      });
      this.on(document, 'shopify:block:select', (event) => {
        if (this.contains(event.target)) this.requestOpen(true);
      });
      const formResult = this.querySelector('[data-popup-result]');
      if (formResult && location.hash === `#${formResult.closest('form').id}`) {
        this.requestOpen(true); return;
      }
      if (this.config.designMode) {
        if (this.config.editorPreview) this.later(() => this.requestOpen(true), 700);
        return;
      }
      if (!this.eligible()) return;
      if (this.config.trigger === 'manual') return;
      const ready = () => {
        if (this.config.trigger === 'delay') this.requestOpen();
        if (this.config.trigger === 'scroll') {
          const check = () => {
            const range = document.documentElement.scrollHeight - innerHeight;
            if (range > 0 && scrollY / range * 100 >= this.config.scroll) this.requestOpen();
          };
          this.on(window, 'scroll', check, { passive: true }); check();
        }
        if (this.config.trigger === 'exit') {
          if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
            this.on(document, 'mouseout', (event) => {
              if (!event.relatedTarget && event.clientY <= 0) this.requestOpen();
            });
          }
        }
      };
      this.later(ready, this.config.delay * 1000);
    }
    on(target, type, handler, options = {}) {
      target.addEventListener(type, handler, { ...options, signal: this.signal });
    }
    later(fn, delay) {
      const id = setTimeout(() => { this.timers.delete(id); fn(); }, delay);
      this.timers.add(id);
    }
    eligible(ignoreFrequency = false) {
      const c = this.config;
      if (!c.enabled || !c.allowed) return false;
      const mobile = matchMedia('(max-width: 749px)').matches;
      if ((mobile && !c.mobile) || (!mobile && !c.desktop)) return false;
      const parse = (value) => value ? Date.parse(value) : null;
      const start = parse(c.start), end = parse(c.end), now = Date.now();
      if ((c.start && !Number.isFinite(start)) || (c.end && !Number.isFinite(end))) return false;
      if ((start !== null && now < start) || (end !== null && now >= end)) return false;
      if (c.audience === 'guest' && c.customer) return false;
      if (c.audience === 'customer' && !c.customer) return false;
      if (c.audience === 'unsubscribed' && c.subscribed) return false;
      const codes = (value) => value.toUpperCase().split(/[\s,;]+/).filter(Boolean);
      if (c.countries && !codes(c.countries).includes(c.country.toUpperCase())) return false;
      if (c.languages && !codes(c.languages).includes(c.language.toUpperCase().split('-')[0])) return false;
      const clean = (path) => path.replace(/\/+$/, '') || '/';
      const matches = (list) => list.split(/[\n,]+/).map(x => x.trim()).filter(Boolean).some(x =>
        x.endsWith('*') ? location.pathname.startsWith(x.slice(0, -1)) : clean(location.pathname) === clean(x));
      if (c.includePaths && !matches(c.includePaths)) return false;
      if (c.excludePaths && matches(c.excludePaths)) return false;
      if (ignoreFrequency) return true;
      try {
        if (localStorage.getItem(`${this.key}:never`)) return false;
        if (c.frequency === 'session' && sessionStorage.getItem(this.key)) return false;
        const previous = Number(localStorage.getItem(this.key));
        if (c.frequency === 'days' && previous && now - previous < c.days * 86400000) return false;
      } catch (_) {}
      return true;
    }
    blocked() {
      return document.hidden || [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], .shopify-pc__banner__dialog')].some(node =>
        node !== this.dialog && !node.closest('[hidden], [aria-hidden="true"]') && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
    }
    requestOpen(manual = false) {
      if (this.dialog.open || this.pending || (!manual && (this.shown || !this.eligible()))) return;
      if (!this.config.designMode && manual && !this.eligible(true)) return;
      const attempt = () => {
        if (!this.isConnected) return;
        if (this.blocked()) { this.later(attempt, 1000); return; }
        this.pending = false;
        if (!this.config.designMode && !this.eligible() && !manual) return;
        this.querySelectorAll('template[data-popup-media]').forEach(template => {
          template.replaceWith(template.content.cloneNode(true));
        });
        this.previousFocus = document.activeElement;
        this.dialog.showModal();
        this.shown = true;
        this.ownsLock = true;
        this.oldOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        if (!this.config.designMode) {
          try {
            sessionStorage.setItem(this.key, '1'); localStorage.setItem(this.key, String(Date.now()));
          } catch (_) {}
        }
      };
      this.pending = true; attempt();
    }
    close(remember = true) {
      if (!this.dialog.open) return;
      if (remember && !this.config.designMode && this.querySelector('[data-popup-never]')?.checked) {
        try { localStorage.setItem(`${this.key}:never`, '1'); } catch (_) {}
      }
      this.dialog.close();
      this.releaseLock();
    }
    releaseLock() {
      if (!this.ownsLock) return;
      this.ownsLock = false;
      document.documentElement.style.overflow = this.oldOverflow || '';
      if (!document.querySelector('dialog[open]') && this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    }
    async copy(button) {
      const code = button.dataset.popupCopy;
      const status = button.closest('[data-popup-coupon]').querySelector('[role="status"]');
      try {
        await navigator.clipboard.writeText(code); status.textContent = this.config.copied;
      } catch (_) { status.textContent = `${this.config.copyFallback} ${code}`; }
    }
    disconnectedCallback() {
      this.close(false); this.abort?.abort(); this.timers?.forEach(clearTimeout);
    }
  }
  customElements.define('ikao-popup', IkaoPopup);
})();
